// v3.15-1205 COMP-012 — Spiff Engine admin.
import { useCallback, useEffect, useState } from 'react';
import { Loader2, Award, Plus, Play, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import api from '../lib/api';
import { Button } from '../components/ui/button';
import { Dialog, DialogContent } from '../components/ui/dialog';
import { Input } from '../components/ui/input';

const SPIFF_TYPES = ['cash', 'gift_card', 'trip', 'one_time'];

const fmtMoney = (n) => Number(n || 0).toLocaleString('en-US', { style: 'currency', currency: 'USD' });
const fmtDate = (iso) => (iso ? iso.slice(0, 10) : '—');

const NewPlanModal = ({ open, onClose, onSaved }) => {
  const [companyId, setCompanyId] = useState('');
  const [name, setName]           = useState('');
  const [eff, setEff]             = useState(new Date().toISOString().slice(0, 10));
  const [notes, setNotes]         = useState('');
  const [saving, setSaving]       = useState(false);
  const submit = async () => {
    if (!companyId || !name) { toast.error('Company + Name required'); return; }
    setSaving(true);
    try {
      await api.post('/spiff-engine/plans', { company_id: companyId, name, effective_date: eff, notes: notes || undefined });
      toast.success('Spiff plan created'); onSaved?.(); onClose();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Save failed'); }
    finally { setSaving(false); }
  };
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg">
        <div className="p-2">
          <h2 className="text-xl font-extrabold text-slate-900 mb-3">New Spiff Plan</h2>
          <label className="block text-[11px] font-bold uppercase text-slate-500 mb-1">Company ID</label>
          <Input data-testid="sp-plan-company" value={companyId} onChange={(e) => setCompanyId(e.target.value)} className="mb-3" placeholder="UUID" />
          <label className="block text-[11px] font-bold uppercase text-slate-500 mb-1">Plan Name</label>
          <Input data-testid="sp-plan-name" value={name} onChange={(e) => setName(e.target.value)} className="mb-3" placeholder="Q3 Contest Spiff" />
          <label className="block text-[11px] font-bold uppercase text-slate-500 mb-1">Effective Date</label>
          <Input data-testid="sp-plan-eff" type="date" value={eff} onChange={(e) => setEff(e.target.value)} className="mb-3" />
          <label className="block text-[11px] font-bold uppercase text-slate-500 mb-1">Notes</label>
          <Input data-testid="sp-plan-notes" value={notes} onChange={(e) => setNotes(e.target.value)} />
          <div className="flex justify-end gap-2 mt-5">
            <Button variant="ghost" onClick={onClose} disabled={saving}>Cancel</Button>
            <Button data-testid="sp-plan-save" onClick={submit} disabled={saving} className="bg-blue-600 hover:bg-blue-700 text-white">
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Create'}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

const NewRuleModal = ({ open, onClose, onSaved, planId }) => {
  const [tt, setTt]         = useState('cash');
  const [crit, setCrit]     = useState('');
  const [amt, setAmt]       = useState('');
  const [eff, setEff]       = useState(new Date().toISOString().slice(0, 10));
  const [notes, setNotes]   = useState('');
  const [saving, setSaving] = useState(false);
  const submit = async () => {
    if (!amt || Number(amt) <= 0) { toast.error('Fixed amount > 0 required'); return; }
    setSaving(true);
    try {
      await api.post(`/spiff-engine/plans/${planId}/rules`, {
        trigger_type: tt, qualification_criteria: crit || undefined,
        fixed_amount: Number(amt), effective_date: eff, notes: notes || undefined,
      });
      toast.success('Spiff rule added'); onSaved?.(); onClose();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Save failed'); }
    finally { setSaving(false); }
  };
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg">
        <div className="p-2">
          <h2 className="text-xl font-extrabold text-slate-900 mb-3">Add Spiff Rule</h2>
          <label className="block text-[11px] font-bold uppercase text-slate-500 mb-1">Trigger Type</label>
          <select data-testid="sp-rule-type" value={tt} onChange={(e) => setTt(e.target.value)}
                  className="w-full mb-3 border border-slate-300 rounded-lg px-3 py-2 text-sm">
            {SPIFF_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
          </select>
          <label className="block text-[11px] font-bold uppercase text-slate-500 mb-1">Qualification Criteria</label>
          <Input data-testid="sp-rule-crit" value={crit} onChange={(e) => setCrit(e.target.value)} placeholder="Top rep of quarter, etc." className="mb-3" />
          <label className="block text-[11px] font-bold uppercase text-slate-500 mb-1">Fixed Amount</label>
          <Input data-testid="sp-rule-amt" type="number" step="0.01" value={amt} onChange={(e) => setAmt(e.target.value)} className="mb-3" />
          <label className="block text-[11px] font-bold uppercase text-slate-500 mb-1">Effective Date</label>
          <Input data-testid="sp-rule-eff" type="date" value={eff} onChange={(e) => setEff(e.target.value)} className="mb-3" />
          <label className="block text-[11px] font-bold uppercase text-slate-500 mb-1">Notes</label>
          <Input data-testid="sp-rule-notes" value={notes} onChange={(e) => setNotes(e.target.value)} />
          <div className="flex justify-end gap-2 mt-5">
            <Button variant="ghost" onClick={onClose} disabled={saving}>Cancel</Button>
            <Button data-testid="sp-rule-save" onClick={submit} disabled={saving} className="bg-blue-600 hover:bg-blue-700 text-white">
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Add Rule'}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

const TriggerModal = ({ open, onClose, onDone, plan }) => {
  const [ruleId, setRuleId]         = useState(plan?.rules?.[0]?.id || '');
  const [ctx, setCtx]               = useState('');
  const [bd, setBd]                 = useState(new Date().toISOString().slice(0, 10));
  const [empIds, setEmpIds]         = useState('');
  const [note, setNote]             = useState('');
  const [saving, setSaving]         = useState(false);
  if (!plan) return null;
  const run = async () => {
    if (!ruleId || !ctx || !empIds) { toast.error('Rule, context, employees required'); return; }
    setSaving(true);
    try {
      const { data } = await api.post('/spiff-engine/trigger', {
        spiff_plan_id: plan.id, spiff_rule_id: ruleId,
        employee_ids: empIds.split(',').map(s => s.trim()).filter(Boolean),
        qualification_context: ctx, business_date: bd, note: note || undefined,
      });
      toast.success(`Spiff: ${data.created_count} issued, ${data.skipped_count} skipped`);
      onDone?.(); onClose();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Trigger failed'); }
    finally { setSaving(false); }
  };
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg">
        <div className="p-2">
          <h2 className="text-xl font-extrabold text-slate-900 mb-3">Issue Spiff — {plan.name}</h2>
          <label className="block text-[11px] font-bold uppercase text-slate-500 mb-1">Rule</label>
          <select data-testid="sp-trigger-rule" value={ruleId} onChange={(e) => setRuleId(e.target.value)}
                  className="w-full mb-3 border border-slate-300 rounded-lg px-3 py-2 text-sm">
            <option value="">— Select rule —</option>
            {(plan.rules || []).map((r) => (
              <option key={r.id} value={r.id}>{r.trigger_type} · {fmtMoney(r.fixed_amount)}</option>
            ))}
          </select>
          <label className="block text-[11px] font-bold uppercase text-slate-500 mb-1">Qualification Context</label>
          <Input data-testid="sp-trigger-ctx" value={ctx} onChange={(e) => setCtx(e.target.value)} placeholder="2026-Q3 Contest Winner" className="mb-3" />
          <label className="block text-[11px] font-bold uppercase text-slate-500 mb-1">Business Date</label>
          <Input data-testid="sp-trigger-date" type="date" value={bd} onChange={(e) => setBd(e.target.value)} className="mb-3" />
          <label className="block text-[11px] font-bold uppercase text-slate-500 mb-1">Employee IDs</label>
          <Input data-testid="sp-trigger-employees" value={empIds} onChange={(e) => setEmpIds(e.target.value)} placeholder="uuid1,uuid2,..." className="mb-3" />
          <label className="block text-[11px] font-bold uppercase text-slate-500 mb-1">Note</label>
          <Input data-testid="sp-trigger-note" value={note} onChange={(e) => setNote(e.target.value)} />
          <div className="flex justify-end gap-2 mt-5">
            <Button variant="ghost" onClick={onClose} disabled={saving}>Cancel</Button>
            <Button data-testid="sp-trigger-run" onClick={run} disabled={saving} className="bg-emerald-600 hover:bg-emerald-700 text-white">
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Play className="w-4 h-4 mr-1" /> Issue</>}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default function SpiffEnginePage() {
  const [loading, setLoading] = useState(true);
  const [plans, setPlans]     = useState([]);
  const [events, setEvents]   = useState([]);
  const [tab, setTab]         = useState('plans');
  const [error, setError]     = useState(null);
  const [newPlan, setNewPlan] = useState(false);
  const [newRuleFor, setNewRuleFor] = useState(null);
  const [triggerFor, setTriggerFor] = useState(null);

  const fetchAll = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      const [p, e] = await Promise.all([
        api.get('/spiff-engine/plans'),
        api.get('/spiff-engine/events', { params: { limit: 500 } }),
      ]);
      setPlans(p?.data?.items || []);
      setEvents(e?.data?.items || []);
    } catch (err) {
      if (err?.response?.status === 403) setError('Spiff administration is restricted to admins.');
      else toast.error('Failed to load spiff data');
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const delRule = async (planId, ruleId) => {
    try {
      await api.delete(`/spiff-engine/plans/${planId}/rules/${ruleId}`);
      toast.success('Rule removed'); fetchAll();
    } catch { toast.error('Delete failed'); }
  };

  return (
    <div className="space-y-4" data-testid="spiff-engine-page">
      <div className="flex items-start justify-between">
        <div className="flex items-center gap-3">
          <Award className="w-6 h-6 text-violet-600" />
          <div>
            <h1 className="text-2xl font-extrabold text-slate-900">Spiff Engine</h1>
            <p className="text-sm text-slate-500">
              Configure Spiff Plans & Rules. Issued spiffs become immutable
              Spiff Events, flow through the Compensation Ledger, and pay via
              existing Payroll.
            </p>
          </div>
        </div>
        <Button data-testid="sp-new-plan-btn" onClick={() => setNewPlan(true)}
                className="bg-blue-600 hover:bg-blue-700 text-white">
          <Plus className="w-4 h-4 mr-2" /> New Spiff Plan
        </Button>
      </div>

      {error ? (
        <div className="p-6 bg-rose-50 text-rose-700 border border-rose-200 rounded-xl">{error}</div>
      ) : loading ? (
        <div className="p-10 flex items-center justify-center text-slate-500 bg-white rounded-2xl border">
          <Loader2 className="w-5 h-5 animate-spin mr-2" /> Loading…
        </div>
      ) : (
        <>
          <div className="flex items-center gap-1 border-b border-slate-200">
            {['plans', 'events'].map((t) => (
              <button key={t} data-testid={`sp-tab-${t}`} onClick={() => setTab(t)}
                      className={
                        'px-4 py-2 text-sm font-semibold border-b-2 -mb-px transition ' +
                        (tab === t ? 'border-violet-600 text-violet-700' : 'border-transparent text-slate-500 hover:text-slate-800')
                      }>{t === 'plans' ? 'Plans & Rules' : 'Spiff Events'}</button>
            ))}
          </div>
          {tab === 'plans' ? (
            plans.length === 0 ? (
              <div className="p-10 text-center text-slate-500 bg-white border rounded-2xl" data-testid="sp-plans-empty">
                No spiff plans yet.
              </div>
            ) : (
              <div className="space-y-4">
                {plans.map((p) => (
                  <div key={p.id} data-testid={`sp-plan-${p.id}`} className="bg-white rounded-2xl border border-slate-200 p-4">
                    <div className="flex items-center justify-between">
                      <div>
                        <h3 className="text-lg font-bold text-slate-800">{p.name}</h3>
                        <p className="text-xs text-slate-500">
                          Company {p.company_id?.slice(0, 8)}… · Effective {fmtDate(p.effective_date)} · Status {p.status}
                        </p>
                      </div>
                      <div className="flex items-center gap-2">
                        <Button size="sm" data-testid={`sp-add-rule-${p.id}`} onClick={() => setNewRuleFor(p.id)} variant="outline">
                          <Plus className="w-4 h-4 mr-1" /> Add Rule
                        </Button>
                        <Button size="sm" data-testid={`sp-trigger-${p.id}`} onClick={() => setTriggerFor(p)} disabled={!p.rules?.length}
                                className="bg-emerald-600 hover:bg-emerald-700 text-white">
                          <Play className="w-4 h-4 mr-1" /> Issue
                        </Button>
                      </div>
                    </div>
                    {(p.rules || []).length === 0 ? (
                      <div className="mt-3 text-xs text-slate-400">No rules yet.</div>
                    ) : (
                      <table className="w-full text-sm mt-3">
                        <thead className="bg-slate-50 text-slate-500 uppercase text-[11px] tracking-wider">
                          <tr>
                            <th className="text-left px-3 py-2">Trigger Type</th>
                            <th className="text-left px-3 py-2">Criteria</th>
                            <th className="text-right px-3 py-2">Amount</th>
                            <th className="text-left px-3 py-2">Effective</th>
                            <th className="text-center px-3 py-2">Status</th>
                            <th></th>
                          </tr>
                        </thead>
                        <tbody>
                          {p.rules.map((r) => (
                            <tr key={r.id} data-testid={`sp-rule-${r.id}`} className="border-t border-slate-100">
                              <td className="px-3 py-2 capitalize">{r.trigger_type.replaceAll('_', ' ')}</td>
                              <td className="px-3 py-2 text-xs text-slate-500">{r.qualification_criteria || '—'}</td>
                              <td className="px-3 py-2 text-right font-semibold">{fmtMoney(r.fixed_amount)}</td>
                              <td className="px-3 py-2 text-xs">{fmtDate(r.effective_date)}</td>
                              <td className="px-3 py-2 text-center text-xs">{r.status}</td>
                              <td className="px-3 py-2 text-right">
                                <button data-testid={`sp-del-rule-${r.id}`} onClick={() => delRule(p.id, r.id)}
                                        className="text-rose-500 hover:text-rose-700"><Trash2 className="w-4 h-4" /></button>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    )}
                  </div>
                ))}
              </div>
            )
          ) : (
            <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-x-auto">
              {events.length === 0 ? (
                <div className="p-10 text-center text-slate-500" data-testid="sp-events-empty">No spiff events yet.</div>
              ) : (
                <table className="w-full text-sm" data-testid="sp-events-table">
                  <thead className="bg-slate-50 text-slate-500 uppercase text-[11px] tracking-wider">
                    <tr>
                      <th className="text-left px-3 py-2">Date</th>
                      <th className="text-left px-3 py-2">Employee</th>
                      <th className="text-left px-3 py-2">Plan</th>
                      <th className="text-left px-3 py-2">Type</th>
                      <th className="text-left px-3 py-2">Context</th>
                      <th className="text-right px-3 py-2">Amount</th>
                    </tr>
                  </thead>
                  <tbody>
                    {events.map((e) => (
                      <tr key={e.id} className="border-t border-slate-100">
                        <td className="px-3 py-2 whitespace-nowrap">{fmtDate(e.business_date)}</td>
                        <td className="px-3 py-2">{e.employee_name || e.employee_id}</td>
                        <td className="px-3 py-2">{e.spiff_plan_name}</td>
                        <td className="px-3 py-2 capitalize">{(e.trigger_type || '').replaceAll('_', ' ')}</td>
                        <td className="px-3 py-2 text-xs">{e.qualification_context}</td>
                        <td className="px-3 py-2 text-right font-semibold text-emerald-700">{fmtMoney(e.amount)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          )}
        </>
      )}
      <NewPlanModal open={newPlan} onClose={() => setNewPlan(false)} onSaved={fetchAll} />
      <NewRuleModal open={!!newRuleFor} onClose={() => setNewRuleFor(null)} onSaved={fetchAll} planId={newRuleFor} />
      <TriggerModal open={!!triggerFor} onClose={() => setTriggerFor(null)} onDone={fetchAll} plan={triggerFor} />
    </div>
  );
}
